import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HighchartsReact from "highcharts-react-official";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ColorPicker,
  Collapse,
  Divider,
  Input,
  Modal,
  Select,
  Space,
  Tooltip,
  Typography,
  message,
} from "antd";
import { Content } from "antd/es/layout/layout";
import { FaChartColumn } from "react-icons/fa6";
// FaSave não existe no pacote fa6 (lá o equivalente é FaFloppyDisk); o
// resto do sistema também importa este ícone de "react-icons/fa".
import { FaSave } from "react-icons/fa";
import { DeleteOutlined, DownloadOutlined, PictureOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

import Highcharts from "./gerador/highcharts";
import { MODELOS, MODELOS_POR_ID, construirOpcoes, opcoesModelo } from "./gerador/modelos";
import { ALTURA_SAIDA, LARGURA_SAIDA, PALETA_SERIES } from "./gerador/tema";
import { dadosParaTexto, parseTabela } from "./gerador/parseTabela";
import { baixarJpeg, gerarJpeg, montarHtmlImagem, salvarNoBanco } from "./gerador/exportar";
import { excluirModelo, listarModelos, salvarModelo } from "./gerador/api";
import EditorSegmentos from "./gerador/EditorSegmentos";
import PainelEstilo from "./gerador/PainelEstilo";
import PainelFundo from "./gerador/PainelFundo";
import PainelVariacoes from "./gerador/PainelVariacoes";

const { Text } = Typography;

/**
 * Chave usada por outras telas (ex.: USDA NASS por estado) para abrir o
 * gerador já com um gráfico montado. Navegação na mesma janela: é o que
 * mantém o gancho de área de transferência do editor quando tudo está num
 * popup aberto pela matéria.
 */
export const CHAVE_SPEC_IMPORTADO = "rb-gerador-spec-importado";

function lerSpecImportado() {
  try {
    const bruto = sessionStorage.getItem(CHAVE_SPEC_IMPORTADO);
    const spec = bruto ? JSON.parse(bruto) : null;
    return spec && MODELOS_POR_ID[spec.modelo] ? spec : null;
  } catch {
    return null;
  }
}

/**
 * Gerador de imagens de gráfico.
 *
 * Substitui a montagem manual em Excel/PowerPoint: o usuário cola uma
 * tabela, escolhe um dos modelos da casa, ajusta cores e textos, e o
 * sistema exporta o JPEG no padrão (604x366), salva no banco de imagens
 * com editoria gf01 e insere na matéria aberta.
 *
 * Quando aberto pelo editor (com ?popup=1), o botão "Inserir na matéria"
 * escreve o HTML no clipboard: é o gancho que openReportPopupAndCapture já
 * intercepta, então o editor cuida da inserção na posição do cursor sem
 * precisar de código novo do lado dele.
 */
export default function GeradorGraficos() {
  const [specInicial] = useState(() => lerSpecImportado() || MODELOS[0].exemplo());
  const [spec, setSpec] = useState(specInicial);
  const [textoTabela, setTextoTabela] = useState(() =>
    dadosParaTexto(specInicial.dados, "Categoria")
  );
  // Remove só depois de montar: em StrictMode o inicializador roda duas
  // vezes, e apagar ali deixaria a segunda chamada sem o gráfico importado.
  useEffect(() => {
    try {
      sessionStorage.removeItem(CHAVE_SPEC_IMPORTADO);
    } catch {}
  }, []);
  // Sem cabeçalho, a primeira linha colada já é dado e as séries recebem
  // nomes genéricos. Marcado por padrão porque é o formato que sai do Excel.
  const [temCabecalho, setTemCabecalho] = useState(true);
  const [erroTabela, setErroTabela] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [modelosSalvos, setModelosSalvos] = useState([]);
  const [nomeModelo, setNomeModelo] = useState("");
  const [modalSalvar, setModalSalvar] = useState(false);
  const [legenda, setLegenda] = useState("Rural Business");
  const [comentario, setComentario] = useState("");
  const chartRef = useRef(null);

  const ehPopup = useMemo(
    () => new URLSearchParams(window.location.search).get("popup") === "1",
    []
  );

  const opcoes = useMemo(() => {
    try {
      return construirOpcoes(spec);
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [spec]);

  // Ajustes que o chart.update() não desfaz: ele MESCLA as opções, e o que
  // some das novas (ex.: o deslocamento da caixa voltando a 0, ou o
  // intervalo do eixo voltando ao automático) fica como estava. Mudando
  // qualquer um deles o gráfico é recriado, como já acontece ao trocar de
  // família.
  const chaveRecriar = useMemo(
    () =>
      JSON.stringify([
        spec?.modelo,
        spec?.eixo || {},
        spec?.rotulo?.deslocX ?? 0,
        spec?.rotulo?.deslocY ?? 0,
      ]),
    [spec]
  );

  const atualizar = useCallback((mudanca) => {
    setSpec((anterior) => ({ ...anterior, ...mudanca }));
  }, []);

  const carregarModelos = useCallback(async () => {
    try {
      setModelosSalvos(await listarModelos());
    } catch (err) {
      console.error("Erro ao carregar modelos salvos:", err);
    }
  }, []);

  useEffect(() => {
    carregarModelos();
  }, [carregarModelos]);

  /** Troca de família preservando o que faz sentido preservar. */
  const trocarModelo = (id) => {
    const modelo = MODELOS_POR_ID[id];
    if (!modelo) return;
    const base = modelo.exemplo();
    setSpec({
      ...base,
      // Mantém o que o usuário já escreveu e os dados que já colou — só o
      // estilo específico da família é trocado.
      titulo: spec.titulo,
      subtitulo: spec.subtitulo,
      fonte: spec.fonte,
      dados: spec.dados?.categorias?.length ? spec.dados : base.dados,
      // O resto de `extras` é específico da família (seta, funil, rampa) e
      // vem do modelo novo; a observação e as variações são trabalho do
      // usuário e acompanham, como o título e a fonte — as variações valem
      // em qualquer família, então não há por que perdê-las na troca.
      extras: {
        ...base.extras,
        ...(spec.extras?.rodape ? { rodape: spec.extras.rodape } : {}),
        ...(spec.extras?.corRodape ? { corRodape: spec.extras.corRodape } : {}),
        ...(spec.extras?.variacoes?.length ? { variacoes: spec.extras.variacoes } : {}),
      },
    });
  };

  const aplicarTabela = (texto, comCabecalho = temCabecalho) => {
    setTextoTabela(texto);
    const resultado = parseTabela(texto, { temCabecalho: comCabecalho });
    setErroTabela(resultado.erro);
    if (!resultado.categorias.length) return;

    // Preserva as cores já escolhidas por posição de série.
    const anteriores = spec.dados?.series || [];
    atualizar({
      dados: {
        categorias: resultado.categorias,
        series: resultado.series.map((serie, i) => ({
          ...serie,
          cor: anteriores[i]?.cor || PALETA_SERIES[i % PALETA_SERIES.length].cor,
          corFim: anteriores[i]?.corFim || PALETA_SERIES[i % PALETA_SERIES.length].corFim,
        })),
      },
    });
  };

  /** Reinterpreta o texto já colado quando o checkbox muda. */
  const alterarCabecalho = (marcado) => {
    setTemCabecalho(marcado);
    aplicarTabela(textoTabela, marcado);
  };

  const alterarSerie = (indice, campo, valor) => {
    const series = (spec.dados?.series || []).map((s, i) =>
      i === indice ? { ...s, [campo]: valor } : s
    );
    atualizar({ dados: { ...spec.dados, series } });
  };

  /** Exporta o JPEG e envia para o banco de imagens. Devolve a URL. */
  const gerarEEnviar = async () => {
    const chart = chartRef.current?.chart;
    if (!chart) throw new Error("O gráfico ainda não terminou de desenhar.");
    const blob = await gerarJpeg(chart);
    return salvarNoBanco(blob, { legenda, comentario });
  };

  const aoSalvarNoBanco = async () => {
    setGerando(true);
    try {
      const { url } = await gerarEEnviar();
      toast.success("Gráfico salvo no banco de imagens (editoria Gráficos).");
      console.log("URL do gráfico gerado:", url);
    } catch (err) {
      toast.error(err.message || "Não foi possível gerar o gráfico.");
    } finally {
      setGerando(false);
    }
  };

  const aoInserirNaMateria = async () => {
    setGerando(true);
    try {
      const { url } = await gerarEEnviar();
      // A imagem precisa estar salva ANTES: base64 efêmero já foi tentado
      // no sistema e morre ao recarregar a matéria (ver ClimaMapas.js).
      await navigator.clipboard.writeText(montarHtmlImagem(url, legenda));
      toast.success(
        ehPopup
          ? "Gráfico enviado para a matéria!"
          : "Código copiado — cole na matéria."
      );
    } catch (err) {
      toast.error(err.message || "Não foi possível inserir o gráfico.");
    } finally {
      setGerando(false);
    }
  };

  const aoBaixar = async () => {
    setGerando(true);
    try {
      const chart = chartRef.current?.chart;
      const blob = await gerarJpeg(chart);
      baixarJpeg(blob, spec.titulo?.map((s) => s.texto).join("") || "grafico");
    } catch (err) {
      toast.error(err.message || "Não foi possível gerar a imagem.");
    } finally {
      setGerando(false);
    }
  };

  const aoSalvarModelo = async () => {
    if (!nomeModelo.trim()) return message.warning("Dê um nome ao modelo.");
    try {
      await salvarModelo(nomeModelo.trim(), spec);
      toast.success("Modelo salvo.");
      setModalSalvar(false);
      setNomeModelo("");
      carregarModelos();
    } catch (err) {
      toast.error("Não foi possível salvar o modelo.");
    }
  };

  const aoCarregarModelo = (codigo) => {
    const modelo = modelosSalvos.find((m) => m.codigo === codigo);
    if (!modelo?.spec) return;
    setSpec(modelo.spec);
    setTextoTabela(dadosParaTexto(modelo.spec.dados, "Categoria"));
    setErroTabela(null);
  };

  const aoExcluirModelo = (codigo, nome) => {
    Modal.confirm({
      title: "Excluir modelo",
      content: `Remover o modelo "${nome}"? Os gráficos já publicados não são afetados.`,
      okText: "Excluir",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        await excluirModelo(codigo);
        toast.success("Modelo excluído.");
        carregarModelos();
      },
    });
  };

  const familia = MODELOS_POR_ID[spec.modelo];

  return (
    <Content style={{ padding: 16 }}>
      {/*
        O preview acompanha a rolagem: a coluna da esquerda é bem mais alta
        que a da direita, então ao descer para os painéis de baixo (estilo,
        fundo, publicação) o gráfico saía de vista justamente na hora de
        ajustar a aparência.

        Duas condições que fazem o sticky funcionar aqui e que não podem ser
        removidas sem quebrá-lo: o container precisa de align-items
        flex-start (com o stretch padrão o cartão fica da altura da linha
        inteira e não sobra para onde grudar) e nenhum ancestral pode ter
        overflow diferente de visible.

        Sem media query de propósito: quando as colunas ficam lado a lado
        depende também da barra lateral estar recolhida ou não, coisa que
        uma media query de viewport não enxerga. E não precisa: empilhado,
        o preview é o último elemento do container, então não sobra
        percurso para grudar e o sticky se comporta como estático.
      */}
      <style>{`
        .rb-gerador-preview {
          position: sticky;
          top: 12px;
          align-self: flex-start;
        }
      `}</style>
      <Content
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <FaChartColumn size={22} color="#1d5c9d" />
        <Typography.Title level={3} style={{ margin: 0 }}>
          Gerador de Gráficos
        </Typography.Title>
        <Text type="secondary">
          Cole a tabela, escolha o modelo e gere a imagem no padrão da casa.
        </Text>
      </Content>

      <Content style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* ---------------- painel de configuração ---------------- */}
        <Card
          style={{ flex: "1 1 420px", minWidth: 380 }}
          styles={{ body: { padding: 14 } }}
        >
          <Collapse
            defaultActiveKey={["modelo", "dados"]}
            items={[
              {
                key: "modelo",
                label: "Modelo",
                children: (
                  <Content style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <Select
                      style={{ width: "100%" }}
                      value={spec.modelo}
                      onChange={trocarModelo}
                      options={opcoesModelo}
                      showSearch
                      optionFilterProp="label"
                      // Cada opção ocupa duas linhas; sem aumentar a altura
                      // da lista o último modelo fica fora da vista.
                      listHeight={420}
                      optionRender={(opcao) => (
                        <Content>
                          <div style={{ fontWeight: 600 }}>{opcao.data.label}</div>
                          <div style={{ fontSize: 12, color: "#888" }}>
                            {opcao.data.descricao}
                          </div>
                        </Content>
                      )}
                    />
                    {familia?.descricao && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {familia.descricao}
                      </Text>
                    )}

                    <Divider style={{ margin: "6px 0" }} />
                    <Text strong style={{ fontSize: 13 }}>
                      Modelos salvos
                    </Text>
                    <Space.Compact style={{ width: "100%" }}>
                      <Select
                        style={{ width: "100%" }}
                        placeholder={
                          modelosSalvos.length
                            ? "Carregar um modelo salvo"
                            : "Nenhum modelo salvo ainda"
                        }
                        value={null}
                        onChange={aoCarregarModelo}
                        options={modelosSalvos.map((m) => ({
                          value: m.codigo,
                          label: m.nome,
                        }))}
                        optionRender={(opcao) => {
                          const modelo = modelosSalvos.find((m) => m.codigo === opcao.value);
                          return (
                            <Content
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span>{opcao.data.label}</span>
                              <DeleteOutlined
                                style={{ color: "#cc0000" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  aoExcluirModelo(modelo.codigo, modelo.nome);
                                }}
                              />
                            </Content>
                          );
                        }}
                      />
                      <Button icon={<FaSave />} onClick={() => setModalSalvar(true)}>
                        Salvar
                      </Button>
                    </Space.Compact>
                  </Content>
                ),
              },
              {
                key: "dados",
                label: "Dados",
                children: (
                  <Content style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Cole direto do Excel, ou use uma tabela em markdown. A primeira
                      coluna são as categorias; as demais viram séries.
                    </Text>
                    <Checkbox
                      checked={temCabecalho}
                      onChange={(e) => alterarCabecalho(e.target.checked)}
                    >
                      <Text style={{ fontSize: 12 }}>
                        A primeira linha é cabeçalho (nomes das séries)
                      </Text>
                    </Checkbox>
                    <Input.TextArea
                      rows={7}
                      value={textoTabela}
                      onChange={(e) => aplicarTabela(e.target.value)}
                      style={{ fontFamily: "monospace", fontSize: 12 }}
                      placeholder={
                        temCabecalho
                          ? "Bimestre\t2025\t2026\n1ºbim\t21,1\t22,6"
                          : "1ºbim\t21,1\t22,6\n2ºbim\t21,3\t22,6"
                      }
                    />
                    {erroTabela && <Alert type="warning" showIcon message={erroTabela} />}

                    {(spec.dados?.series || []).map((serie, i) => (
                      <Space.Compact key={i} style={{ width: "100%" }}>
                        <Input
                          value={serie.nome}
                          onChange={(e) => alterarSerie(i, "nome", e.target.value)}
                          addonBefore={`Série ${i + 1}`}
                        />
                        <Tooltip title="Cor clara (centro da coluna)">
                          <ColorPicker
                            value={serie.cor}
                            disabledAlpha
                            onChange={(c) => alterarSerie(i, "cor", c.toHexString())}
                          />
                        </Tooltip>
                        <Tooltip title="Cor escura (bordas / base)">
                          <ColorPicker
                            value={serie.corFim}
                            disabledAlpha
                            onChange={(c) => alterarSerie(i, "corFim", c.toHexString())}
                          />
                        </Tooltip>
                      </Space.Compact>
                    ))}
                  </Content>
                ),
              },
              {
                key: "textos",
                label: "Títulos",
                children: (
                  <Content style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <Text strong style={{ fontSize: 13 }}>
                      Título
                    </Text>
                    <EditorSegmentos
                      valor={spec.titulo}
                      rotulo="Título"
                      onChange={(v) => atualizar({ titulo: v })}
                    />
                    <Text strong style={{ fontSize: 13 }}>
                      Subtítulo
                    </Text>
                    <EditorSegmentos
                      valor={spec.subtitulo?.length ? spec.subtitulo : [{ texto: "", cor: "#00D0FF" }]}
                      rotulo="Subtítulo"
                      onChange={(v) => atualizar({ subtitulo: v })}
                    />
                    <Input
                      addonBefore="Fonte"
                      value={spec.fonte}
                      onChange={(e) => atualizar({ fonte: e.target.value })}
                      placeholder="Fonte: RURAL BUSINESS"
                    />

                    <Text strong style={{ fontSize: 13 }}>
                      Observação (rodapé)
                    </Text>
                    <Space.Compact style={{ width: "100%" }}>
                      <Input
                        value={spec.extras?.rodape || ""}
                        onChange={(e) =>
                          atualizar({
                            extras: { ...(spec.extras || {}), rodape: e.target.value },
                          })
                        }
                        placeholder="* BB boiadas de boa terminação e logística."
                      />
                      <Tooltip title="Cor da observação">
                        <ColorPicker
                          value={spec.extras?.corRodape || "#F0F000"}
                          disabledAlpha
                          onChange={(c) =>
                            atualizar({
                              extras: {
                                ...(spec.extras || {}),
                                corRodape: c.toHexString(),
                              },
                            })
                          }
                        />
                      </Tooltip>
                    </Space.Compact>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Aparece embaixo do gráfico. O espaço é reservado
                      automaticamente quando há texto.
                    </Text>
                  </Content>
                ),
              },
              {
                key: "estilo",
                label: "Estilo e valores",
                children: <PainelEstilo spec={spec} onChange={atualizar} />,
              },
              {
                key: "variacoes",
                label: "Variações",
                children: <PainelVariacoes spec={spec} onChange={atualizar} />,
              },
              {
                key: "fundo",
                label: "Imagem de fundo",
                children: <PainelFundo spec={spec} onChange={atualizar} />,
              },
              {
                key: "publicar",
                label: "Publicação",
                children: (
                  <Content style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <Input
                      addonBefore="Legenda"
                      value={legenda}
                      onChange={(e) => setLegenda(e.target.value)}
                    />
                    <Input
                      addonBefore="Comentário"
                      value={comentario}
                      onChange={(e) => setComentario(e.target.value)}
                      placeholder="Palavras-chave para achar a imagem depois"
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      A imagem é salva na editoria <b>Gráficos</b> e fica disponível no
                      banco de imagens para reuso.
                    </Text>
                  </Content>
                ),
              },
            ]}
          />
        </Card>

        {/* ---------------- preview e ações ---------------- */}
        <Card
          className="rb-gerador-preview"
          style={{ flex: "1 1 640px", minWidth: 420 }}
          styles={{ body: { padding: 14 } }}
        >
          <Content
            style={{
              // O gráfico é sempre desenhado em 604x366 — o mesmo tamanho da
              // exportação — e só encolhido visualmente. Redimensionar o
              // gráfico faria o preview deixar de corresponder à imagem.
              width: "100%",
              overflowX: "auto",
              background: "#2b2b2b",
              padding: 10,
              borderRadius: 4,
            }}
          >
            <Content style={{ width: LARGURA_SAIDA, height: ALTURA_SAIDA, margin: "0 auto" }}>
              {opcoes ? (
                <HighchartsReact
                  // A key força recriar o gráfico ao trocar de família.
                  // Sem isso o HighchartsReact faz chart.update(), que
                  // MESCLA as opções: um gráfico de barras herdava o
                  // options3d do modelo de cilindro anterior (a moldura 3D
                  // aparecia por cima) e o events.render do desenho
                  // customizado não era religado — a seta de variação
                  // simplesmente não era desenhada. Também recria nos
                  // ajustes de eixo/posição (ver chaveRecriar).
                  key={chaveRecriar}
                  ref={chartRef}
                  highcharts={Highcharts}
                  options={opcoes}
                />
              ) : (
                <Alert type="error" message="Não foi possível montar o gráfico." />
              )}
            </Content>
          </Content>

          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            Saída: {LARGURA_SAIDA}×{ALTURA_SAIDA} px (o dobro do padrão histórico, para
            ficar nítido em telas modernas — na matéria ocupa os mesmos 302 px de sempre).
          </Text>

          <Space wrap style={{ marginTop: 12 }}>
            <Button
              type="primary"
              icon={<PictureOutlined />}
              loading={gerando}
              onClick={aoInserirNaMateria}
              style={{ fontWeight: "bold" }}
            >
              {ehPopup ? "Inserir na matéria" : "Gerar e copiar"}
            </Button>
            <Button icon={<FaSave />} loading={gerando} onClick={aoSalvarNoBanco}>
              Salvar no banco de imagens
            </Button>
            <Button icon={<DownloadOutlined />} loading={gerando} onClick={aoBaixar}>
              Baixar JPG
            </Button>
          </Space>
        </Card>
      </Content>

      <Modal
        open={modalSalvar}
        title="Salvar como modelo"
        okText="Salvar"
        cancelText="Cancelar"
        onOk={aoSalvarModelo}
        onCancel={() => setModalSalvar(false)}
      >
        <Text type="secondary">
          O modelo guarda o estilo e os dados atuais. Depois é só carregar e trocar os
          números.
        </Text>
        <Input
          style={{ marginTop: 10 }}
          value={nomeModelo}
          onChange={(e) => setNomeModelo(e.target.value)}
          placeholder="Ex.: Soja - média por bimestre"
          onPressEnter={aoSalvarModelo}
        />
      </Modal>
    </Content>
  );
}
